/**
 * Doses taken, as a percentage that cannot be absurd.
 *
 * Clamped because this figure gets read aloud to a prescriber, and the failure
 * it prevents has now happened twice from two different causes — once from
 * dividing by the wrong numerator, once from stored data where more doses were
 * counted as taken than were ever scheduled. Both printed a percentage above
 * 100 on a page a clinician was looking at.
 *
 * The upstream causes are fixed. This stays anyway: a screen that renders
 * "125% taken · -1 not" is not reporting a number, it is reporting that
 * something is broken, and it should not do that to someone mid-appointment.
 */
export function adherencePercent(taken: number, scheduled: number): number | null {
  if (!Number.isFinite(taken) || !Number.isFinite(scheduled) || scheduled <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((taken / scheduled) * 100)));
}

/**
 * Which day the fold is showing, said in the label.
 *
 * It always read "How today's going", which was true only while it always
 * showed today. Now that the strip can point it elsewhere — and that it falls
 * back to yesterday on a brand-new day — a fixed label would quietly mislabel
 * somebody else's Tuesday as their own morning.
 *
 * THE ONE LABEL (22 Oct 2026). The closed card on the night face named this
 * fold "How today's going" in its own words while the fold, after midnight,
 * said "How yesterday went". Both read it from here now.
 */
export function foldLabel(day: string, today: string): string {
  if (day === today) return "How today's going";
  if (day === dayBefore(today)) return 'How yesterday went';
  return `How ${new Date(`${day}T12:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
  })} went`;
}

/** Local midday, so a DST hop cannot skip a date. */
function dayBefore(day: string): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString('en-CA');
}
